import React, { useState, useRef, useEffect } from 'react';
import LeftArrowIcon from '../Icons/LeftArrowIcon';
import DanteLogoIcon from '../Icons/DanteLogoIcon';
import FullDanteLogoIcon from '../Icons/FullDanteLogoIcon';
import VerticalOptionsMenuIcon from '../Icons/VerticalOptionsMenuIcon';
import CloseIcon from '../Icons/CloseIcon';
import OnlineStatusIcon from '../Icons/OnlineStatusIcon';
import DocsIcon from '../Icons/DocsIcon';
import ResetIcon from '../Icons/ResetIcon';
import MuteSoundIcon from '../Icons/MuteSound';
import SoundIcon from '../Icons/SoundIcon';
import MinimizeIcon from '../Icons/MinimizeIcon';
import { useSoundEffects } from '@/hooks/useSoundEffects';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import ChevronDownIcon from '../Icons/ChevronDownIcon';
import StatusMenu from '../StatusMenu';
import * as humanHandoverService from '@/services/humanHandover';
import EmailIcon from '../Icons/EmailIcon';
import EmailTranscriptIcon from '../Icons/EmailTranscriptIcon';

const BubbleHeader = ({
  type = 'chat',
  chatbotName = 'Dante AI',
  customIcon,
  onBackClick,
  onMenuClick,
  onCloseClick,
  onMinimizeClick,
  className = '',
  isInHumanHandover = false,
  agentName = '',
  agentTitle = '',
  agentAvatar = null,
  agentJoined = false,
  conversationTitle = '',
  onResetChat,
  onViewDocs,
  onPrivacyPolicy,
  onTermsOfService,
  onEmailTranscriptClick,
  showDropdown = true,
  config = null,
  isPreviewMode = false,
  showPasswordModal = false,
  isInHumanHandoverApp = false,
  hh_conversation_id = null,
  currentCategoryId = null,
  isIframe = false,
  dropdownItems = [
    { label: 'Reset Chat', icon: ResetIcon, onClick: null },
    { label: 'Privacy Policy', icon: DocsIcon, onClick: null },
    { label: 'Terms of Service', icon: DocsIcon, onClick: null },
  ],
  ...props
}) => {

  // State for dropdown visibility (legacy support)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Initialize sound effects with config data
  const { soundConfig, updateSoundConfig } = useSoundEffects({ customizationData: config });

  // Sound toggle function
  const toggleSound = () => {
    updateSoundConfig({
      enabled: !soundConfig.enabled
    });
  };

  // Filter out Reset Chat when in Human Handover or iframe
  const filteredDropdownItems = dropdownItems.filter(item => {
    if (isInHumanHandover || isInHumanHandoverApp || isIframe) {
      return item.label !== 'Reset Chat';
    }
    return true;
  });

  const handleMenuClick = () => {
    // Don't allow menu interactions in preview mode
    if (isPreviewMode) {
      return;
    }

    if (showDropdown) {
      setIsDropdownOpen(!isDropdownOpen);
    }
    if (onMenuClick) {
      onMenuClick();
    }
  };

  const handleDropdownItemClick = (item) => {
    if (item.onClick) {
      item.onClick();
    } else if (item.label === 'Reset Chat' && onResetChat) {
      onResetChat();
    } else if (item.label === 'Privacy Policy') {
      if (onPrivacyPolicy) {
        onPrivacyPolicy();
      } else if (onViewDocs) {
        onViewDocs();
      } else {
        // Open privacy policy link from config or default
        const url = config?.privacy_policy_link || 'https://www.dante-ai.com/privacy-policy';
        window.open(url, '_blank');
      }
    } else if (item.label === 'Terms of Service') {
      if (onTermsOfService) {
        onTermsOfService();
      } else if (onViewDocs) {
        onViewDocs();
      } else {
        // Open terms of service link from config or default
        const url = config?.terms_of_use_link || 'https://www.dante-ai.com/terms-of-service';
        window.open(url, '_blank');
      }
    } else if (item.label === 'Email Transcript' && onEmailTranscriptClick) {
      onEmailTranscriptClick();
    } else if (item.label === 'Unmute Notifications' || item.label === 'Mute Notifications') {
      toggleSound();
    }
  };

  const handleStatusChange = async (hh_category_id) => {
    try {
      const response = await humanHandoverService.updateCategoryConversation(
        hh_conversation_id,
        hh_category_id
      );
      if (response.status === 200) {
        // setCategoryId(hh_category_id)
      }
    } catch (error) {
      console.error(error);
    }
  };

  // Helper to render chatbot name, possibly as a link
  const renderChatbotName = (className, style) => {
    if (config?.link_logo_to_website && config?.brand_website) {
      return (
        <a
          href={config.brand_website}
          target="_blank"
          rel="noopener noreferrer"
          className={className}
          style={style}
        >
          {chatbotName}
        </a>
      );
    }
    return (
      <span className={className} style={style}>
        {chatbotName}
      </span>
    );
  };

  const renderChatHeader = () => (
    <div
      className="flex w-full h-v3-huge px-v3-sm py-v3-sm justify-between items-center border-v3-black-border  relative"
      style={{
        backgroundColor: config?.surface_color || 'transparent',
      }}
    >
      {/* Left side with back button and content */}
      <div className="flex items-center gap-v3-xs">
        {/* Back button - only show if onBackClick is provided */}
        {onBackClick && (
          <button
            onClick={onBackClick}
            className="flex w-v3-lg h-v3-lg justify-center items-center gap-v3-xs aspect-square rounded-v3-sm  transition-colors hover:bg-[var(--hover-bg-color)]"
            style={{
              '--hover-bg-color': config?.response_background_color || 'var(--v3-black-border)',
            }}
          >
            <LeftArrowIcon style={{
              color: config?.element_color || '#000000',
            }} />
          </button>
        )}

        {/* Content wrapper */}
        <div className="flex items-center gap-size2">
          {/* Icon */}
          {config?.chatbot_icon ? (
            <img
              src={config.chatbot_icon instanceof File ? URL.createObjectURL(config.chatbot_icon) : config.chatbot_icon}
              alt="Chatbot Icon"
              className="w-v3-lg h-v3-lg object-cover rounded-full"
            />
          ) : customIcon || <DanteLogoIcon />}

          {/* Chatbot name */}
          <div className='flex flex-col max-w-[200px]'>
            <div className='flex items-center gap-[6px]'>
              {renderChatbotName(
                "text-sm text-v3-black-max font-medium truncate",
                {
                  fontFamily: config?.font_name ? `${config.font_name}, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif` : undefined,
                  fontSize: config?.font_size ? `${config.font_size}px` : undefined,
                  color: config?.element_color || 'var(--v3-black-max)',
                }
              )}
              <OnlineStatusIcon className="flex-shrink-0" />
            </div>
            <div
              className='text-xs text-v3-black-secondary leading-4 font-medium'
              style={{
                fontFamily: config?.font_name || undefined,
                color: config?.element_color ? `${config.element_color}B3` : 'var(--v3-black-secondary)',
              }}
            >
              AI Agent
            </div>
          </div>
        </div>
      </div>

      {/* Right side with menu and close buttons */}
      <div className="flex items-center gap-v3-xs relative">
        {showDropdown && (
          <Menu>
            <MenuButton
              className="bubble-dropdown-button flex w-v3-lg h-v3-lg justify-center items-center gap-v3-xs aspect-square rounded-v3-sm transition-colors hover:bg-[var(--hover-bg-color)]"
              style={{
                '--hover-bg-color': config?.response_background_color || 'var(--v3-black-border)',
              }}
            >
           <VerticalOptionsMenuIcon className="bubble-dropdown-icon" style={{
                color: config?.element_color || '#000000',
              }} />
            </MenuButton>
            <MenuItems className="bubble-dropdown-menu absolute top-full right-0 mt-v3-xs z-[9999] w-[205px] p-v3-xxs flex flex-col items-start rounded-v3-lg bg-[#FFFFFF] shadow-v3-double text-v3-black-max">
              <div className="w-full">
                {config?.sound_enabled && (
                  <MenuItem>
                    {({ active }) => (
                      <button
                        onClick={() => handleDropdownItemClick({
                          label: soundConfig.enabled ? 'Mute Notifications' : 'Unmute Notifications'
                        })}
                        className={`bubble-dropdown-item bubble-dropdown-sound w-full flex items-center gap-v3-xs p-v3-xs text-left text-sm font-medium transition-colors rounded-v3-lg ${
                          active ? 'bg-v3-black-border' : 'bg-white'
                        }`}
                      >
                        {soundConfig.enabled ?
                          <MuteSoundIcon style={{ color: config?.brand_color || '#5745FA' }} /> :
                          <SoundIcon style={{ color: config?.brand_color || '#5745FA' }} />
                        }
                        <span>{soundConfig.enabled ? 'Mute Notifications' : 'Unmute Notifications'}</span>
                      </button>
                    )}
                  </MenuItem>
                )}

                {config?.show_email_details && (
                  <MenuItem>
                    {({ active }) => (
                      <button
                        onClick={() => handleDropdownItemClick({
                          label: 'Email Transcript'
                        })}
                        className={`bubble-dropdown-item bubble-dropdown-email w-full flex items-center gap-v3-xs p-v3-xs text-left text-sm font-medium transition-colors rounded-v3-lg ${
                          active ? 'bg-v3-black-border' : 'bg-[#FFFFFF]'
                        }`}
                      >
                        <EmailTranscriptIcon style={{
                          color: config?.brand_color || '#5745FA'
                        }} />
                        <span>Email Transcript</span>
                      </button>
                    )}
                  </MenuItem>     
                )}        
  
              
                {filteredDropdownItems.map((item, index) => {
                  // Generate specific class based on item label
                  const itemClass = `bubble-dropdown-${item.label.toLowerCase().replace(/\s+/g, '-')}`;
                  return (
                  <MenuItem key={index}>
                    {({ active }) => (
                      <button
                        onClick={() => handleDropdownItemClick(item)}
                        className={`bubble-dropdown-item ${itemClass} w-full flex items-center gap-v3-xs p-v3-xs text-left text-sm font-medium transition-colors rounded-v3-lg ${
                          active ? 'bg-v3-black-border' : 'bg-[#FFFFFF]'
                        }`}
                      >
                        <item.icon style={{
                          color: config?.brand_color || '#5745FA'
                        }} />
                        <span>{item.label}</span>
                      </button>
                    )}
                  </MenuItem>
                  );
                })}
              </div>
            </MenuItems>
          </Menu>
        )}
        {/* Close button - hide when in iframe */}
        {!isIframe && (
          <button
            onClick={onCloseClick}
            className="flex w-v3-lg h-v3-lg justify-center items-center gap-v3-xs aspect-square rounded-v3-sm  transition-colors hover:bg-[var(--hover-bg-color)]"
            style={{
              '--hover-bg-color': config?.response_background_color || 'var(--v3-black-border)',
            }}
          >
            <CloseIcon style={{
              color: config?.element_color || '#000000',
            }} />
          </button>
        )}
      </div>
    </div>
  );

  const renderHomeHeader = () => (
    <div
      className="flex w-full px-v3-sm py-v3-sm justify-between items-center z-10"
      style={{
        backgroundColor: config?.surface_color || '#FFFFFF',
      }}
    >
      {/* Left side with logo and chatbot name */}
      <div className="flex items-center gap-v3-sm z-50">
        {/* Show small icon when chatbot name is visible, full logo when not */}
        {
        (config?.embed_logo_url ? (
            <img
              src={config.embed_logo_url instanceof File ? URL.createObjectURL(config.embed_logo_url) : config.embed_logo_url}
              alt="Header Logo"
              className="bubble-header-logo w-auto h-[28px] object-contain"
            />
          ) : <DanteLogoIcon className="bubble-header-logo" />
        )}

        {/* Chatbot name */}
        {config?.show_chatbot_name && (
          config?.link_logo_to_website && config?.brand_website ? (
            <a
              href={config.brand_website}
              target="_blank"
              rel="noopener noreferrer"
              className="text-v3-h6 truncate max-w-[250px]"
              style={{
                fontFamily: config?.font_name ? `${config.font_name}, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif` : undefined,
                fontSize: config?.font_size ? `${config.font_size}px` : undefined,
                color: config?.element_color || 'var(--v3-black-max)',
                textDecoration: 'none',
              }}
            >
              {chatbotName}
            </a>
          ) : (
            <span
              className="text-v3-h6 truncate max-w-[250px]"
              style={{
                fontFamily: config?.font_name ? `${config.font_name}, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif` : undefined,
                fontSize: config?.font_size ? `${config.font_size}px` : undefined,
                color: config?.element_color || 'var(--v3-black-max)',
              }}
            >
              {chatbotName}
            </span>
          )
        )}
      </div>
      
      {/* Right side with close button */}
      <div className="flex items-center gap-v3-xs">
        <button
          onClick={onCloseClick}
          className="flex w-v3-lg  h-v3-lg justify-center items-center gap-v3-xs aspect-square rounded-v3-sm hover:bg-[var(--hover-bg-color)] transition-colors"
          style={{
            '--hover-bg-color': config?.response_background_color || 'var(--v3-black-border)',
          }}
        >
          <CloseIcon style={{
            color: config?.element_color || '#000000',
          }} />
        </button>
      </div>
    </div>
  );

  const renderMessagesHeader = () => (
    <div
      className="flex w-full px-v3-sm py-v3-sm justify-between items-center border-v3-black-border bg-[#FFFFFF] relative z-20"
      style={{ backgroundColor: '#FFFFFF !important' }}
    >
      {/* Left side with logo and title */}
      <div className="flex items-center gap-v3-sm">
        {/* {config?.chatbot_icon ? (
          <img
            src={config.chatbot_icon instanceof File ? URL.createObjectURL(config.chatbot_icon) : config.chatbot_icon}
            alt="Chatbot Icon"
            className="w-v3-lg h-v3-lg object-cover rounded-full"
          />
        ) : <DanteLogoIcon />} */}
        <span
          className="text-v3-h6"
          style={{
            color: config?.element_color || 'var(--v3-black-max)',
            fontFamily: config?.font_name ? `${config.font_name}, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif` : undefined,
            fontSize: config?.font_size ? `${config.font_size}px` : undefined,
          }}
        >
          Messages
        </span>
      </div>

      {/* Right side with close button */}
      <div className="flex items-center gap-v3-xs">
        <button
          onClick={onCloseClick}
          className="flex w-v3-lg h-v3-lg justify-center items-center gap-v3-xs aspect-square rounded-v3-sm hover:bg-[var(--hover-bg-color)] transition-colors"
          style={{
            '--hover-bg-color': config?.response_background_color || 'var(--v3-black-border)',
          }}
        >
          <CloseIcon style={{
            color: config?.element_color || '#000000',
          }} />
        </button>
      </div>
    </div>
  );

  const renderHumanHandoverHeader = () => (
    <div className="flex max-w-[440px] px-v3-sm py-v3-sm justify-between items-center bg-[#FFFFFF] relative w-full rounded-v3-md">
      {/* Left side with back button and agent info */}
      <div className="flex items-center gap-v3-xs">
        {/* Back button - only show if onBackClick is provided and not in human handover */}
        {onBackClick && !isInHumanHandover && (
          <button
            onClick={onBackClick}
            className="flex w-v3-lg h-v3-lg justify-center items-center gap-v3-xs aspect-square rounded-v3-sm transition-colors hover:bg-[var(--hover-bg-color)]"
            style={{
              '--hover-bg-color': config?.response_background_color || 'var(--v3-black-border)',
            }}
          >
            <LeftArrowIcon className='text-v3-purple-primary' />
          </button>
        )}

        {/* Agent info */}
        <div className="flex items-center gap-v3-sm">
          {/* Agent avatar */}
          <div className={`w-v3-lg h-v3-lg bg-v3-black-subtle flex items-center justify-center ${isInHumanHandoverApp ? 'bg-v3-black-border rounded-v3-sm' : 'rounded-full'}`}>
            {isInHumanHandoverApp ? (
              <StatusMenu className="relative" onStatusChange={handleStatusChange} currentCategoryId={currentCategoryId} />
            ) : agentAvatar ? (
              <img
                src={agentAvatar}
                alt={agentName}
                className="w-full h-full object-cover"
              />
            ) : config?.chatbot_icon ? (
              <img
                src={config.chatbot_icon instanceof File ? URL.createObjectURL(config.chatbot_icon) : config.chatbot_icon}
                alt="Chatbot Icon"
                className="w-full h-full object-cover rounded-full"
              />
            ) : (
              <DanteLogoIcon />
            )}
          </div>

          {/* Agent name and title */}
          <div className="flex flex-col max-w-[200px]">
            <div className="flex items-center gap-1">
              <span className="text-sm font-medium text-v3-black-max font-inter-variable truncate">
                {agentName ?? chatbotName}
              </span>
              {/* Online status indicator */}
              <OnlineStatusIcon className="flex-shrink-0" />
            </div>
            <span className="text-xs font-medium text-[#6B7280] leading-4 font-inter-variable truncate">
              {agentTitle ?? 'AI Agent'}
            </span>
          </div>
        </div>
      </div>

      {/* Right side with menu and close buttons */}
      <div className="flex items-center gap-v3-xs relative">
        {/* Menu */}
        {showDropdown && (
          <Menu>
            <MenuButton
              className="bubble-dropdown-button flex w-v3-lg h-v3-lg justify-center items-center gap-v3-xs aspect-square rounded-v3-sm transition-colors hover:bg-[var(--hover-bg-color)]"
              style={{
                '--hover-bg-color': config?.response_background_color || 'var(--v3-black-border)',
              }}
            >
              <VerticalOptionsMenuIcon className="bubble-dropdown-icon" />
            </MenuButton>
            <MenuItems className="bubble-dropdown-menu absolute top-full right-0 mt-v3-xs z-[9999] w-[205px] p-v3-xxs flex flex-col items-start rounded-v3-lg bg-[#FFFFFF] shadow-v3-double">
              <div className="w-full">
                {config?.sound_enabled && (
                  <MenuItem>
                    {({ active }) => (
                      <button
                        onClick={() => handleDropdownItemClick({
                          label: soundConfig.enabled ? 'Mute Notifications' : 'Unmute Notifications'
                        })}
                        className={`bubble-dropdown-item bubble-dropdown-sound w-full flex items-center gap-v3-xs p-v3-xs text-left text-sm font-medium transition-colors rounded-v3-lg ${
                          active ? 'bg-v3-black-border' : 'bg-[#FFFFFF]'
                        }`}
                      >
                        {soundConfig.enabled ?
                          <MuteSoundIcon style={{ color: config?.brand_color || '#5745FA' }} /> :
                          <SoundIcon style={{ color: config?.brand_color || '#5745FA' }} />
                        }
                        <span>{soundConfig.enabled ? 'Mute Notifications' : 'Unmute Notifications'}</span>
                      </button>
                    )}
                  </MenuItem>
                )}
                {filteredDropdownItems.map((item, index) => {
                  // Generate specific class based on item label
                  const itemClass = `bubble-dropdown-${item.label.toLowerCase().replace(/\s+/g, '-')}`;
                  return (
                  <MenuItem key={index}>
                    {({ active }) => (
                      <button
                        onClick={() => handleDropdownItemClick(item)}
                        className={`bubble-dropdown-item ${itemClass} w-full flex items-center gap-v3-xs p-v3-xs text-left text-sm font-medium transition-colors rounded-v3-lg ${
                          active ? 'bg-v3-black-border' : 'bg-[#FFFFFF]'
                        }`}
                      >
                        <item.icon />
                        <span>{item.label}</span>
                      </button>
                    )}
                  </MenuItem>
                  );
                })}
              </div>
            </MenuItems>
          </Menu>
        )}

        {/* Minimize button - only show in human handover mode */}
        {isInHumanHandover && onMinimizeClick && (
          <button
            onClick={onMinimizeClick}
            className="flex w-v3-lg h-v3-lg justify-center items-center gap-v3-xs aspect-square rounded-v3-xxl transition-colors"
          >
            <MinimizeIcon  className='w-5 h-5'/>
          </button>
        )}

        {/* Close button */}
        <button
          onClick={onCloseClick}
          className="flex w-v3-lg h-v3-lg justify-center items-center gap-v3-xs aspect-square rounded-v3-sm hover:bg-[var(--hover-bg-color)]  transition-colors"
          style={{
            '--hover-bg-color': config?.response_background_color || 'var(--v3-black-border)',
          }}
        >
          <CloseIcon />
        </button>
      </div>
    </div>
  );

  const renderPreviousConversationsHeader = () => (
    <div className="flex w-full px-v3-sm py-v3-sm justify-between items-center bg-[#FFFFFF] relative z-20">
      {/* Left side with conversation title */}
      <div className="flex items-center">
        <span className="text-v3-h6 text-v3-black-max">
          {conversationTitle || ''}
        </span>
      </div>

      {/* Right side with close button only */}
      <div className="flex items-center">
        <button
          onClick={onCloseClick}
          className="flex w-v3-lg h-v3-lg justify-center items-center gap-v3-xs aspect-square rounded-v3-xxl  transition-colors"
        >
          <CloseIcon />
        </button>
      </div>
    </div>
  );

  const renderPasswordModalHeader = () => (
    <div
      className="flex w-full px-v3-sm py-v3-sm justify-between items-center"
      style={{
        backgroundColor: config?.surface_color || '#FFFFFF',
      }}
    >
      {/* Left side with logo */}
      <div className="flex items-center gap-v3-sm">
        {config?.chatbot_icon ? (
          <img
            src={config.chatbot_icon instanceof File ? URL.createObjectURL(config.chatbot_icon) : config.chatbot_icon}
            alt="Chatbot Icon"
            className="bubble-header-icon w-v3-lg h-v3-lg object-cover rounded-full"
          />
        ) : customIcon || <DanteLogoIcon className="bubble-header-logo" />}
      </div>

      {/* Right side with close button */}
      <div className="flex items-center gap-v3-xs">
        <button
          onClick={onCloseClick}
          className="flex w-v3-lg h-v3-lg justify-center items-center gap-v3-xs aspect-square rounded-v3-xxl transition-colors"
        >
          <CloseIcon />
        </button>
      </div>
    </div>
  );

  const renderHeader = () => {
    // If password modal is shown, show simplified header with only logo and close button
    if (showPasswordModal) {
      return renderPasswordModalHeader();
    }

    // If in human handover mode, show the human handover header regardless of type
    if (isInHumanHandover) {
      return renderHumanHandoverHeader();
    }

    switch (type) {
      case 'chat':
        return renderChatHeader();
      case 'home':
        return renderHomeHeader();
      case 'messages':
        return renderMessagesHeader();
      case 'previous-conversations':
        return renderPreviousConversationsHeader();
      case 'human-handover':
        return renderHumanHandoverHeader();
      default:
        return renderChatHeader();
    }
  };

  return (
    <div className={`bubble-header ${className} rounded-v3-lg`} {...props}>
      {renderHeader()}
    </div>
  );
};

export default BubbleHeader;
